import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import moment from 'moment';
import {
    Box,
    Card,
    Checkbox,
    Divider,
    Dropdown,
    IconButton,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    ScrollableTableSortableHeaderCells,
    Stack,
    Tag,
    Text,
} from 'shell/components';
import { getUserPreferredFullName, getUserPronoun } from 'utils/helper';
import { COLUMNS } from './MemberTable';

const getPrettyRole = (role) => {
    return role === 'student' ? 'Student' : 'Instructor';
};

const getLastActivity = (activity) => {
    if (!activity) return '--';
    return moment(activity.created_on).format('MMM D[,] YYYY');
};

const MemberList = ({
    accountUsers,
    checked,
    setChecked,
    getMemberActionOptions,
    onSelect,
    setAccountUsers,
    sortState,
    handleSetSortState,
}) => {
    const { device } = useViewport();

    const renderMobile = () => {
        return (
            <Stack spacing={3}>
                {(accountUsers || []).map((accountUser) => (
                    <Card key={accountUser.owner_account_id}>
                        <Stack sx={{ width: '100%' }} spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                <Stack
                                    alignItems={'flex-start'}
                                    direction={'row'}
                                    justifyContent={'flex-start'}
                                    spacing={1}
                                >
                                    <Checkbox
                                        aria-label={`select ${
                                            getUserPreferredFullName(accountUser) || accountUser.email
                                        }`}
                                        checked={checked.includes(accountUser.owner_account_id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setChecked((current) => [...current, accountUser.owner_account_id]);
                                            } else {
                                                setChecked((current) =>
                                                    current.filter(
                                                        (ownerAccountId) =>
                                                            ownerAccountId !== accountUser.owner_account_id
                                                    )
                                                );
                                            }
                                        }}
                                    />
                                    <Stack>
                                        <Text component={'p'} variant={'sh2'}>
                                            {getUserPreferredFullName(accountUser)}
                                        </Text>
                                        <Text color={'light'}>{accountUser.email}</Text>
                                    </Stack>
                                </Stack>
                                <Dropdown
                                    id={`table-dropdown-${accountUser.owner_account_id}`}
                                    options={getMemberActionOptions(accountUser)}
                                    rootComponent={
                                        <IconButton
                                            aria-label={`more options for ${
                                                getUserPreferredFullName(accountUser) || accountUser.email
                                            }`}
                                            kind={'moreHorizontal'}
                                            size={'medium'}
                                            variant={'square'}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    onSelect={({ key }) => onSelect(key, accountUser)}
                                />
                            </Stack>
                            <Divider />
                            <Stack spacing={2}>
                                <Text color={'light'}>{`Role: ${accountUser.role}`}</Text>
                                <Text color={'light'}>{`Last Activity: ${getLastActivity(
                                    accountUser.login_activity
                                )}`}</Text>
                            </Stack>
                        </Stack>
                    </Card>
                ))}
            </Stack>
        );
    };

    const renderTabletDesktop = () => {
        return (
            <CustomScrollableTable aria-label={'list of course members'} size={'small'}>
                <ScrollableTableHead>
                    <ScrollableTableRow>
                        <ScrollableTableCell isFixed>
                            <Checkbox
                                aria-label={'select all members'}
                                checked={accountUsers?.every((accountUser) =>
                                    checked.includes(accountUser.owner_account_id)
                                )}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setChecked(accountUsers?.map(({ owner_account_id }) => owner_account_id));
                                    } else {
                                        setChecked([]);
                                    }
                                }}
                            />
                        </ScrollableTableCell>

                        <ScrollableTableSortableHeaderCells
                            columns={COLUMNS}
                            sortState={sortState}
                            handleSetSortState={handleSetSortState}
                        />

                        <ScrollableTableCell isFixed>Actions</ScrollableTableCell>
                    </ScrollableTableRow>
                </ScrollableTableHead>
                <ScrollableTableBody>
                    {(accountUsers || []).map((accountUser) => (
                        <ScrollableTableRow key={accountUser.owner_account_id}>
                            <ScrollableTableCell sx={{ pr: 0 }}>
                                <Checkbox
                                    aria-label={`select ${getUserPreferredFullName(accountUser) || accountUser.email}`}
                                    checked={checked.includes(accountUser.owner_account_id)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setChecked((current) => [...current, accountUser.owner_account_id]);
                                        } else {
                                            setChecked((current) =>
                                                current.filter(
                                                    (ownerAccountId) => ownerAccountId !== accountUser.owner_account_id
                                                )
                                            );
                                        }
                                    }}
                                />
                            </ScrollableTableCell>
                            <ScrollableTableCell noWrap>
                                {getUserPreferredFullName(accountUser) ? (
                                    <>
                                        {getUserPreferredFullName(accountUser)}{' '}
                                        {accountUser.pronoun ? `(${getUserPronoun(accountUser.pronoun)})` : ''}
                                    </>
                                ) : (
                                    <>{accountUser.email}</>
                                )}
                            </ScrollableTableCell>
                            <ScrollableTableCell>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <CustomBox>{accountUser.email}</CustomBox>
                                    {!accountUser.is_pending ? <Tag type={'invitation'} value={'pending'} /> : null}
                                </Stack>
                            </ScrollableTableCell>
                            <ScrollableTableCell>{getPrettyRole(accountUser.role)}</ScrollableTableCell>
                            <ScrollableTableCell>{getLastActivity(accountUser.login_activity)}</ScrollableTableCell>
                            <ScrollableTableCell>
                                <Dropdown
                                    id={`table-dropdown-${accountUser.owner_account_id}`}
                                    options={getMemberActionOptions(accountUser)}
                                    rootComponent={
                                        <IconButton
                                            aria-label={`more options for ${
                                                getUserPreferredFullName(accountUser) || accountUser.email
                                            }`}
                                            kind={'moreHorizontal'}
                                            size={'medium'}
                                            variant={'square'}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    onSelect={({ key }) => onSelect(key, accountUser)}
                                />
                            </ScrollableTableCell>
                        </ScrollableTableRow>
                    ))}
                </ScrollableTableBody>
            </CustomScrollableTable>
        );
    };

    return device === 'mobile' ? renderMobile() : renderTabletDesktop();
};

const CustomBox = styled(Box)(() => {
    return {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    };
});

const CustomScrollableTable = styled(ScrollableTable)(({ theme }) => {
    return {
        width: '100%',
        minHeight: 'calc(100vh - 145px)',
        '& .MuiTableCell-root': {
            '&:not(:last-child)': {
                borderRight: `1px solid ${theme.palette.ensightful.neutral[300]}`,
            },
            '&:nth-child(1)': {
                width: '73px',
            },
            '&:nth-child(2)': {
                width: '40%',
            },
            '&:last-child': {
                width: '56px',
            },
        },
    };
});

export default MemberList;
